import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AimieRobotAvatarIcon } from '../chat/ChatAvatarIcons';
import './GuidedHighlightHoverTooltip.css';

const HIGHLIGHT_SELECTOR = '[data-guided-highlight-purpose]';

function GuidedHighlightHoverTooltip({ theme = 'dark' }) {
  const [tooltip, setTooltip] = useState(null);
  const hasShownRef = useRef(false);

  useEffect(() => {
    const showFor = (target) => {
      if (hasShownRef.current) return;
      const anchor = target instanceof Element ? target.closest(HIGHLIGHT_SELECTOR) : null;
      if (!anchor) return;
      const purpose = anchor.getAttribute('data-guided-highlight-purpose');
      if (!purpose) return;
      hasShownRef.current = true;
      setTooltip({ purpose, rect: anchor.getBoundingClientRect() });
    };
    const hide = () => setTooltip(null);
    const handlePointerOut = (event) => {
      const currentAnchor = event.target instanceof Element
        ? event.target.closest(HIGHLIGHT_SELECTOR)
        : null;
      const nextAnchor = event.relatedTarget instanceof Element
        ? event.relatedTarget.closest(HIGHLIGHT_SELECTOR)
        : null;
      if (currentAnchor && currentAnchor === nextAnchor) return;
      hide();
    };
    const handlePointerOver = (event) => showFor(event.target);
    const handleFocusIn = (event) => showFor(event.target);
    const handleFocusOut = (event) => {
      const nextAnchor = event.relatedTarget instanceof Element
        ? event.relatedTarget.closest(HIGHLIGHT_SELECTOR)
        : null;
      if (nextAnchor) return;
      hide();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') hide();
    };

    document.addEventListener('pointerover', handlePointerOver);
    document.addEventListener('pointerout', handlePointerOut);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', hide);
    document.addEventListener('click', hide);
    window.addEventListener('resize', hide);
    document.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerover', handlePointerOver);
      document.removeEventListener('pointerout', handlePointerOut);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', hide);
      document.removeEventListener('click', hide);
      window.removeEventListener('resize', hide);
      document.removeEventListener('scroll', hide, true);
    };
  }, []);

  if (!tooltip) return null;

  const width = Math.min(300, window.innerWidth - 24);
  const left = Math.max(12, Math.min(
    window.innerWidth - width - 12,
    tooltip.rect.left + (tooltip.rect.width / 2) - (width / 2),
  ));
  const openAbove = tooltip.rect.bottom + 150 > window.innerHeight && tooltip.rect.top > 150;
  const positionStyle = openAbove
    ? { width, left, bottom: window.innerHeight - tooltip.rect.top + 10 }
    : { width, left, top: tooltip.rect.bottom + 10 };

  return createPortal(
    <div className={`guided-highlight-hover-tooltip ${theme}`} style={positionStyle} role="tooltip">
      <div className="guided-highlight-hover-tooltip__message">
        <span className="guided-highlight-hover-tooltip__avatar" aria-hidden="true">
          <AimieRobotAvatarIcon />
        </span>
        <div>
          <p className="guided-highlight-hover-tooltip__meaning">
            This option may be a helpful next step.
          </p>
          <p className="guided-highlight-hover-tooltip__purpose">{tooltip.purpose}</p>
        </div>
      </div>
      <div className="guided-highlight-hover-tooltip__footer">
        Hide or restore these: <strong>☰ → Settings → Guided Highlights</strong>
      </div>
    </div>,
    document.body,
  );
}

export default GuidedHighlightHoverTooltip;
